/**
 * Emergency Override Dialog — W3-A · B7 · governed direct-Active path.
 *
 * Only rendered when the current user holds
 * ``workflow:emergency_override`` (surfaced from the parent).
 *
 * Governance rules enforced at the UI layer (backend re-enforces):
 *   • Reason must be ≥ 20 characters.
 *   • The dialog clearly identifies that the action creates an
 *     Active Project through an exceptional route.
 *   • Shows the daily override limit read from the workflow
 *     definition (fallback 3/day).
 *   • Requires explicit confirmation checkbox before submit.
 *
 * Submits to POST /api/projects/engagement/emergency-override with
 * X-Emergency-Reason header (backend contract).  Returns 201 on
 * success with the newly-created engagement handle.
 *
 * W3-A · B8 · DEFECT-001 (2026-08-03) — internal-type Emergency
 * Project Creation was previously broken because the dialog did
 * not expose Business Unit / Cost Centre / Initiative fields.  The
 * backend rejects internal engagements without an
 * organisational_context, so submissions failed with
 * ``internal_project_requires_organisational_context``.  This patch
 * mirrors the BU/CC/initiative section from ProjectCreateDialog when
 * ``projectType === "internal"``, cascading the CC list from the
 * selected BU, and includes the picked context in the emergency
 * override payload.  Client-type flows are untouched.
 */
import { useEffect, useMemo, useState } from "react";
import { ShieldWarning, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

import { api, formatApiError } from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Checkbox } from "../../../components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";

const DEFAULT_DAILY_CAP = 3;
const MIN_REASON = 20;


function newIdemKey() {
    // Same shape as the client-generated keys used across the app.
    return `eo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}


export default function EmergencyOverrideDialog({ open, onClose, onCreated }) {
    const { user } = useAuth();
    // W3-A · B8 · caller must hold master reads to see the BU/CC pickers
    // when project_type=internal.  Super Admin always does; if for
    // some reason a caller lacks them, we still surface the internal
    // form fields but let the backend enforce completeness — we do
    // NOT hide the fields (the emergency override is a super-admin
    // affordance by policy).
    const perms = useMemo(
        () => new Set(user?.permissions || []),
        [user],
    );
    const canReadMasters = (
        perms.has("bu_master:read") && perms.has("cost_centre_master:read")
    );

    const [clients, setClients] = useState([]);
    const [projectType, setProjectType] = useState("client");
    const [clientId, setClientId] = useState("");
    // W3-A · B8 — organisational context for internal-type EOs.
    const [businessUnits, setBusinessUnits] = useState([]);
    const [costCentres, setCostCentres] = useState([]);
    const [businessUnitId, setBusinessUnitId] = useState("");
    const [costCentreId, setCostCentreId] = useState("");
    const [initiative, setInitiative] = useState("");
    const [ccLoading, setCcLoading] = useState(false);
    const [name, setName] = useState("");
    const [reason, setReason] = useState("");
    const [confirmed, setConfirmed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [idempotencyKey, setIdempotencyKey] = useState(newIdemKey());

    useEffect(() => {
        if (!open) return;
        // Fresh idempotency key + reset state whenever the dialog re-opens.
        setIdempotencyKey(newIdemKey());
        setName("");
        setClientId("");
        setReason("");
        setConfirmed(false);
        setSaving(false);
        setProjectType("client");
        // W3-A · B8 · reset internal-only fields.
        setBusinessUnits([]);
        setCostCentres([]);
        setBusinessUnitId("");
        setCostCentreId("");
        setInitiative("");
        const safeGet = async (url) => {
            try {
                const r = await api.get(url);
                return r.data?.items || r.data || [];
            } catch {
                return [];
            }
        };
        (async () => {
            const [cItems, buItems] = await Promise.all([
                safeGet("/billing/client/list?limit=200"),
                canReadMasters
                    ? safeGet("/org/business-unit/list?page_size=200")
                    : Promise.resolve([]),
            ]);
            setClients(cItems);
            setBusinessUnits(buItems);
        })();
    }, [open, canReadMasters]);

    // W3-A · B8 · cascade: re-fetch cost centres when BU selection
    // changes.  Mirrors ProjectCreateDialog's cascade.
    useEffect(() => {
        if (!open || !businessUnitId || !canReadMasters) {
            setCostCentres([]);
            return;
        }
        setCcLoading(true);
        api.get(
            `/org/cost-centre/list?business_unit_id=${encodeURIComponent(businessUnitId)}&page_size=200`,
        )
            .then((r) => setCostCentres(r.data?.items || []))
            .catch(() => setCostCentres([]))  // fail-soft
            .finally(() => setCcLoading(false));
    }, [open, businessUnitId, canReadMasters]);

    const reasonTooShort = reason.trim().length < MIN_REASON;
    // W3-A · B8 · internal-type submissions require BU/CC/initiative.
    const internalContextComplete = (
        projectType !== "internal"
        || (!!businessUnitId && !!costCentreId && initiative.trim().length > 0)
    );
    const canSubmit = (
        !saving
        && !reasonTooShort
        && confirmed
        && name.trim().length > 0
        && (projectType !== "client" || !!clientId)
        && internalContextComplete
    );

    async function submit() {
        setSaving(true);
        const payload = {
            project_type: projectType,
            name: name.trim(),
        };
        if (projectType === "client") {
            payload.client_id = clientId;
        }
        if (projectType === "internal") {
            // W3-A · B8 · include organisational_context so the
            // backend's `internal_project_requires_organisational_context`
            // guard is satisfied.
            payload.organisational_context = {
                business_unit_id: businessUnitId,
                cost_centre_id: costCentreId,
                initiative: initiative.trim(),
            };
        }
        try {
            const r = await api.post(
                "/projects/engagement/emergency-override",
                payload,
                {
                    headers: {
                        "Idempotency-Key": idempotencyKey,
                        "X-Emergency-Reason": reason.trim(),
                    },
                },
            );
            onCreated?.(r.data);
        } catch (err) {
            const msg = formatApiError(err);
            toast.error(msg || "Emergency override failed");
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                className="max-w-xl"
                data-testid="emergency-override-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-900">
                        <ShieldWarning className="w-6 h-6 text-amber-600" weight="fill" />
                        Emergency Project Creation
                    </DialogTitle>
                    <DialogDescription>
                        This creates an <strong>Active Project</strong>
                        {" "}through an exceptional route <em>without</em>
                        {" "}the ordinary approval process. Every invocation is
                        immutably logged, rate-capped per day, and notifies the
                        initiator and every authorised approver.
                    </DialogDescription>
                </DialogHeader>

                <div
                    className="rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
                    data-testid="emergency-override-warning"
                >
                    <Warning className="w-5 h-5 text-amber-700 mt-0.5" weight="fill" />
                    <div className="text-sm text-amber-900">
                        <div className="font-semibold">Governance limit</div>
                        <div>
                            Up to <strong>{DEFAULT_DAILY_CAP} overrides per user per day</strong>.
                            Reason required (≥ {MIN_REASON} chars).
                            Do <strong>not</strong> use this as a shortcut for routine initiation —
                            every override is reviewed by the governance team.
                        </div>
                    </div>
                </div>

                <div className="space-y-3 pt-2">
                    <div>
                        <Label>Project type</Label>
                        <Select
                            value={projectType}
                            onValueChange={setProjectType}
                        >
                            <SelectTrigger data-testid="eo-project-type">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="client">Client project</SelectItem>
                                <SelectItem value="internal">Internal project</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label>Project name</Label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Urgent regulatory response · Client Alpha"
                            data-testid="eo-project-name"
                        />
                    </div>

                    {projectType === "client" ? (
                        <div>
                            <Label>Client</Label>
                            <Select
                                value={clientId}
                                onValueChange={setClientId}
                            >
                                <SelectTrigger data-testid="eo-client-id">
                                    <SelectValue placeholder="Pick a client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {clients.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>
                                            {c.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    ) : null}

                    {projectType === "internal" ? (
                        // W3-A · B8 · Internal Emergency Project requires
                        // BU / CC / Initiative (backend enforces).
                        // Mirrors ProjectCreateDialog's org-context section.
                        <div
                            className="space-y-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-3"
                            data-testid="eo-internal-context-section"
                        >
                            <div className="text-xs font-medium text-slate-700">
                                Finance context (required for internal projects)
                            </div>
                            {canReadMasters && businessUnits.length === 0 && (
                                <div
                                    className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                                    data-testid="eo-org-masters-empty-banner"
                                >
                                    Organisation masters are not configured.
                                    Configure at least one active Business Unit
                                    and its linked Cost Centre before creating
                                    an internal emergency project.
                                </div>
                            )}
                            <div>
                                <Label htmlFor="eo-bu">
                                    Business Unit <span className="text-rose-600">*</span>
                                </Label>
                                <Select
                                    value={businessUnitId}
                                    onValueChange={(v) => {
                                        setBusinessUnitId(v);
                                        setCostCentreId("");  // cascade reset
                                    }}
                                >
                                    <SelectTrigger
                                        id="eo-bu"
                                        data-testid="eo-business-unit-trigger"
                                    >
                                        <SelectValue placeholder="Choose a Business Unit..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {businessUnits.map((bu) => (
                                            <SelectItem
                                                key={bu.id}
                                                value={bu.id}
                                                data-testid={`eo-bu-option-${bu.id}`}
                                            >
                                                {bu.name} ({bu.code})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="eo-cc">
                                    Cost Centre <span className="text-rose-600">*</span>
                                </Label>
                                <Select
                                    value={costCentreId}
                                    onValueChange={setCostCentreId}
                                    disabled={!businessUnitId || ccLoading}
                                >
                                    <SelectTrigger
                                        id="eo-cc"
                                        data-testid="eo-cost-centre-trigger"
                                    >
                                        <SelectValue placeholder={
                                            !businessUnitId
                                                ? "Select a Business Unit first"
                                                : ccLoading
                                                    ? "Loading..."
                                                    : (costCentres.length === 0
                                                        ? "No Cost Centres for this Business Unit"
                                                        : "Choose a Cost Centre...")
                                        } />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {costCentres.map((cc) => (
                                            <SelectItem
                                                key={cc.id}
                                                value={cc.id}
                                                data-testid={`eo-cc-option-${cc.id}`}
                                            >
                                                {cc.name} ({cc.code})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="eo-initiative">
                                    Initiative <span className="text-rose-600">*</span>
                                </Label>
                                <Input
                                    id="eo-initiative"
                                    data-testid="eo-initiative"
                                    value={initiative}
                                    onChange={(e) => setInitiative(e.target.value)}
                                    placeholder="e.g. Urgent SOC-2 audit remediation"
                                />
                            </div>
                        </div>
                    ) : null}

                    <div>
                        <Label>
                            Reason
                            {reasonTooShort ? (
                                <span className="text-xs text-red-600 ml-2">
                                    ({MIN_REASON - reason.trim().length} more chars required)
                                </span>
                            ) : (
                                <span className="text-xs text-emerald-700 ml-2">
                                    ({reason.trim().length}/{MIN_REASON} chars)
                                </span>
                            )}
                        </Label>
                        <Textarea
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={3}
                            placeholder="Explain why the ordinary workflow cannot be used …"
                            data-testid="eo-reason"
                        />
                    </div>

                    <label
                        className="flex items-start gap-2 text-sm text-slate-700 pt-1"
                        data-testid="eo-confirm-label"
                    >
                        <Checkbox
                            checked={confirmed}
                            onCheckedChange={(v) => setConfirmed(!!v)}
                            data-testid="eo-confirm-checkbox"
                        />
                        <span>
                            I understand this creates an Active Project
                            outside the approval workflow, will be
                            immutably audited, and counts toward my
                            daily override cap.
                        </span>
                    </label>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="eo-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        disabled={!canSubmit}
                        onClick={submit}
                        data-testid="eo-submit-btn"
                    >
                        {saving ? "Creating..." : "Create Emergency Project"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
